import { useSelfContext, useUniversityContext } from '@/context';
import { useCallback, useState } from 'react';
import API from '@/services/APIs';
import { useAppStore, useCartStore } from '@/store';
import { useTranslation } from 'react-i18next';
import { isCartSingleReserveWeek, resolveCartWeekStartMs } from '@/utils/cartWeekValidation';

function isPresent(value: unknown): value is string | number | boolean {
  if (value === undefined || value === null || value === 'undefined' || value === '') {
    return false;
  }
  if (typeof value === 'number' && Number.isNaN(value)) {
    return false;
  }
  return true;
}

/** Week start as epoch ms (Rose checkout). */
function resolveWeekStartTimestampMs(
  stored: number | string | null | undefined,
  showingDate: string | undefined,
  programDate: string | undefined,
): number | null {
  if (typeof stored === 'number' && !Number.isNaN(stored)) {
    return stored;
  }
  if (typeof stored === 'string' && /^\d+$/.test(stored.trim())) {
    return Number(stored.trim());
  }
  if (showingDate?.trim()) {
    return resolveCartWeekStartMs(showingDate.split('+')[0], programDate);
  }
  if (programDate?.trim()) {
    return resolveCartWeekStartMs(undefined, programDate);
  }
  return null;
}

type RoseCheckoutRow = {
  selected: boolean;
  selectedCount: number;
  id: number | string;
  programId: number;
  mealTypeId: number;
  programDateTime: number;
  selfId: number;
  foodTypeId: number;
  foodId: number;
  priorReserveDateStr: string;
  freeFoodSelected: boolean;
};

/** Cart lines only — Rose indices are always 0..n-1 in basket order. */
function buildRoseCheckoutRows(
  basketItems: { selectedCount: number; food: UserWeekReserveEntity }[],
  lineSelfId: number,
): RoseCheckoutRow[] {
  return basketItems.map(item => {
    const food = item.food;
    const selfId = food.selfId ?? lineSelfId;
    const foodId = food.foodId ?? food.foodList?.[0]?.id;
    return {
      selected: true,
      selectedCount: item.selectedCount,
      id: food.id ?? '',
      programId: food.programId,
      mealTypeId: food.mealTypeId,
      programDateTime: food.programDateTime,
      selfId,
      foodTypeId: food.foodTypeId,
      foodId: Number(foodId),
      priorReserveDateStr: food.priorReserveDateStr ?? 'null',
      freeFoodSelected: false,
    };
  });
}

function appendQueryParam(parts: string[], key: string, value: unknown) {
  if (value === undefined || value === null) {
    return;
  }
  if (!isPresent(value) && value !== 0 && value !== false && value !== '') {
    return;
  }
  parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
}

function buildRoseCheckoutQueryParts(params: {
  weekStartDateTime: number;
  remainCredit: number;
  selectedSelfDefId: number;
  rows: RoseCheckoutRow[];
}): string[] {
  const parts: string[] = [];

  appendQueryParam(parts, 'action', 'checkout');
  appendQueryParam(parts, 'weekStartDateTime', params.weekStartDateTime);
  appendQueryParam(parts, 'weekStartDateTimeAjx', params.weekStartDateTime);
  appendQueryParam(parts, 'remainCredit', params.remainCredit);
  appendQueryParam(parts, 'method:doReserve', 'Submit');
  appendQueryParam(parts, 'selectedSelfDefId', params.selectedSelfDefId);

  params.rows.forEach((row, index) => {
    const prefix = `userWeekReserves[${index}]`;
    appendQueryParam(parts, `${prefix}.selected`, row.selected);
    appendQueryParam(parts, `${prefix}.id`, row.id);
    appendQueryParam(parts, `${prefix}.programId`, row.programId);
    appendQueryParam(parts, `${prefix}.mealTypeId`, row.mealTypeId);
    appendQueryParam(parts, `${prefix}.programDateTime`, row.programDateTime);
    appendQueryParam(parts, `${prefix}.selfId`, row.selfId);
    appendQueryParam(parts, `${prefix}.foodTypeId`, row.foodTypeId);
    appendQueryParam(parts, `${prefix}.selectedCount`, row.selectedCount);
    appendQueryParam(parts, `${prefix}.priorReserveDateStr`, row.priorReserveDateStr);
    appendQueryParam(parts, `${prefix}.freeFoodSelected`, row.freeFoodSelected);
    appendQueryParam(parts, `${prefix}.foodId`, row.foodId);
  });

  return parts;
}

export default function useBasketManagement() {
  const [t] = useTranslation(['common']);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const creditValue = useAppStore(state => state.credit);
  const basketItems = useCartStore(state => state.programs);
  const cartSelectedSelfId = useCartStore(state => state.selectedSelfId);
  const checkoutSelfDefId = useCartStore(state => state.checkoutSelfDefId);
  const cartWeekStartDateTime = useCartStore(state => state.weekStartDateTime);
  const { currentSelf, showingDate } = useSelfContext();
  const universityURL = useUniversityContext()?.university?.universityURL;

  const submit = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (basketItems.length === 0) {
        setError(t('common:errors.cart-checkout-incomplete'));
        return;
      }

      if (!universityURL) {
        setError(t('common:errors.connection-error'));
        return;
      }

      if (
        currentSelf?.freeRestaurant
        && !isCartSingleReserveWeek(cartWeekStartDateTime, basketItems)
      ) {
        setError(t('common:errors.cart-multi-week-not-allowed'));
        return;
      }

      const selectedSelfDefId =
        checkoutSelfDefId
        ?? cartSelectedSelfId
        ?? currentSelf?.id
        ?? basketItems[0]?.food?.selfId;

      const weekStartDateTime = resolveWeekStartTimestampMs(
        cartWeekStartDateTime,
        showingDate,
        basketItems[0]?.food?.programDate ?? basketItems[0]?.food?.programDateStr,
      );

      const lineSelfId = Number(
        selectedSelfDefId ?? cartSelectedSelfId ?? currentSelf?.id ?? basketItems[0]?.food?.selfId,
      );

      if (weekStartDateTime == null || !isPresent(selectedSelfDefId)) {
        setError(t('common:errors.cart-checkout-incomplete'));
        return;
      }

      for (const item of basketItems) {
        const food = item.food;
        const selfId = food?.selfId ?? lineSelfId;

        if (
          !isPresent(food?.programId)
          || !isPresent(food?.mealTypeId)
          || !isPresent(food?.programDateTime)
          || !isPresent(selfId)
          || !isPresent(food?.foodTypeId)
          || !isPresent(food?.foodId)
        ) {
          setError(t('common:errors.cart-checkout-incomplete'));
          return;
        }
      }

      const checkoutRows = buildRoseCheckoutRows(basketItems, lineSelfId);

      const token = (await API.User.getLoginToken()).payload.loginAsToken;
      const roseQuery = buildRoseCheckoutQueryParts({
        weekStartDateTime,
        remainCredit: creditValue,
        selectedSelfDefId: Number(selectedSelfDefId),
        rows: checkoutRows,
      }).join('&');

      const redirect = `/nurture/user/multi/reserve/reserve.rose?${roseQuery}`;
      const URL =
        `${universityURL}/j_security_check?loginAsToken=${encodeURIComponent(token)}` +
        `&redirect=${encodeURIComponent(redirect)}`;

      window.open(URL, '_self');
    }
    catch (e) {
      setError(t('common:errors.submit-request'));
      console.error('basket checkout failed', e);
    }
    finally {
      setLoading(false);
    }
  },
  [
    basketItems,
    cartSelectedSelfId,
    cartWeekStartDateTime,
    checkoutSelfDefId,
    creditValue,
    currentSelf?.freeRestaurant,
    currentSelf?.id,
    showingDate,
    universityURL,
    t,
  ],
  );

  return { loading, error, submit };
}
